"use client"

import React, { useState, useEffect } from "react"
import { useParams } from "react-router-dom"
import { 
  ShoppingBag, 
  UtensilsCrossed, 
  Users, 
  DollarSign, 
  Clock, 
  AlertCircle,
  TrendingUp,
  Package,
  Wifi,
  WifiOff,
  Activity,
  QrCode
} from "lucide-react"
import { restaurantPortalAPI } from "../../services/restaurantApiClient"
import { useWebSocket } from "../../hooks/useWebSocket"
import { useRestaurantAuth } from "../../contexts/RestaurantAuthContext"
import { useLanguage } from "../../contexts/LanguageContext"
import PageHeader from "../../components/ui/dashboard/PageHeader"
import StatCard from "../../components/ui/dashboard/StatCard"
import LoadingSpinner from "../../components/ui/dashboard/LoadingSpinner"
import RestaurantQRCodeModal from "../../components/restaurant/RestaurantQRCodeModal"

interface DashboardData {
  restaurant: any
  todayOrders: number
  pendingOrders: number
  foodItemsCount: number
  tablesCount: number
  totalRevenue: number
  recentOrders: any[]
}

const RestaurantDashboardPage: React.FC = () => {
  const { restaurantId } = useParams()
  const { user, token, isOwner } = useRestaurantAuth()
  const { t, language, dir } = useLanguage()
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [isConnected, setIsConnected] = useState(false)
  const [analyticsData, setAnalyticsData] = useState<any>(null)
  const [realtimeStats, setRealtimeStats] = useState<any>(null)
  const [isQrModalOpen, setIsQrModalOpen] = useState(false)

  // WebSocket setup
  const { isConnected: wsConnected } = useWebSocket({
    token: token || undefined,
    userType: "restaurant_owner",
    restaurantId,
    onOrderUpdated: (data) => {
      console.log("Order updated via WebSocket:", data)
      // Refresh dashboard data when order is updated
      fetchDashboardData()
    },
    onOrderReceived: (data) => {
      console.log("New order received via WebSocket:", data)
      // Show notification and refresh dashboard
      fetchDashboardData()
    },
    onDashboardUpdated: (data) => {
      console.log("Dashboard updated via WebSocket:", data)
      // Update specific dashboard data
      if (data.updateType === "stats") {
        setDashboardData(prev => prev ? { ...prev, ...data.data } : null)
      }
    },
    onAuthenticated: () => {
      console.log("WebSocket authenticated")
      setIsConnected(true)
    },
    onError: (error) => {
      console.error("WebSocket error:", error)
      setIsConnected(false)
    },
  })

  const fetchDashboardData = async () => {
    try {
      const [dashboardResponse, analyticsResponse, realtimeResponse] = await Promise.all([
        restaurantPortalAPI.getDashboard(restaurantId!),
        restaurantPortalAPI.getAnalytics(restaurantId!, "7d"),
        restaurantPortalAPI.getRealtimeStats(restaurantId!)
      ])
      
      setDashboardData(dashboardResponse.data)
      setAnalyticsData(analyticsResponse.data)
      setRealtimeStats(realtimeResponse.data)
    } catch (error) {
      console.error("Error fetching dashboard data:", error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (restaurantId) {
      fetchDashboardData()
    }
  }, [restaurantId])

  if (loading) {
    return <LoadingSpinner size="3" text={t("loadingRestaurantDashboard")} />
  }

  if (!dashboardData) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900">{t("noDataAvailable")}</h3>
      </div>
    )
  }

  const stats = [
    {
      title: t("todaysOrders"),
      value: dashboardData.todayOrders,
      icon: ShoppingBag,
      color: "bg-blue-500",
    },
    {
      title: t("pendingOrders"),
      value: dashboardData.pendingOrders,
      icon: Clock,
      color: "bg-orange-500",
    },
    {
      title: t("foodItems"),
      value: dashboardData.foodItemsCount,
      icon: UtensilsCrossed,
      color: "bg-green-500",
    },
    {
      title: t("tables"),
      value: dashboardData.tablesCount,
      icon: Users,
      color: "bg-purple-500",
    },
    {
      title: t("todaysRevenue"),
      value: `SDG ${dashboardData.totalRevenue.toFixed(2)}`,
      icon: DollarSign,
      color: "bg-red-500",
    },
  ]

  return (
    <div className="space-y-6" dir={dir}>
      <PageHeader
        title={`${dashboardData.restaurant.name} ${t("dashboard")}`}
        subtitle={t("manageRestaurantOperations")}
      >
        <div className={`flex items-center ${language === 'ar' ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
          {isOwner && (
            <button
              onClick={() => setIsQrModalOpen(true)}
              className={`flex items-center px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${language === 'ar' ? 'space-x-reverse space-x-2' : 'space-x-2'} bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 shadow-sm`}
            >
              <QrCode className="w-4 h-4" />
              <span>{t('menuQRCode') || 'Menu QR'}</span>
            </button>
          )}
          <div className={`flex items-center px-3 py-1.5 rounded-full text-sm ${language === 'ar' ? 'space-x-reverse space-x-1' : 'space-x-1'} ${
            wsConnected 
              ? 'bg-green-100 text-green-800' 
              : 'bg-red-100 text-red-800'
          }`}>
            {wsConnected ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
            <span>{wsConnected ? t('connected') : t('disconnected')}</span>
          </div>
        </div>
      </PageHeader>

      <RestaurantQRCodeModal
        isOpen={isQrModalOpen}
        onClose={() => setIsQrModalOpen(false)}
        restaurantName={dashboardData.restaurant.name}
        restaurantSlug={dashboardData.restaurant.slug}
      />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon
          return (
            <StatCard
              key={index}
              title={stat.title}
              value={stat.value}
              icon={Icon}
              color={stat.color}
              loading={loading}
            />
          )
        })}
      </div>

      {/* Analytics Widgets */}
      {analyticsData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Selling Items */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                {t('topSellingItems')}
              </h2>
            </div>
            <div className="p-6">
              {analyticsData.topSellingItems.length > 0 ? (
                <div className="space-y-4">
                  {analyticsData.topSellingItems.slice(0, 5).map((item: any, index: number) => (
                    <div
                      key={item.name}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                    >
                      <div className={`flex items-center ${language === 'ar' ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                        <div className="flex items-center justify-center w-6 h-6 bg-red-100 text-red-600 rounded-full text-xs font-bold">
                          {index + 1}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {item.name}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {item.category}
                          </p>
                        </div>
                      </div>
                      <div className={`${language === 'ar' ? 'text-left' : 'text-right'}`}>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {item.quantity} {t('sold')}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">SDG {item.revenue.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <UtensilsCrossed className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">{t('noSalesData')}</p>
                </div>
              )}
            </div>
          </div>

          {/* Revenue Trend */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                {t('revenueTrend')}
              </h2>
            </div>
            <div className="p-6">
              {analyticsData.revenueTrend.length > 0 ? (
                <div className="space-y-3">
                  {analyticsData.revenueTrend.slice(-7).map((day: any) => (
                    <div
                      key={day.date}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                    >
                      <div className={`flex items-center ${language === 'ar' ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                        <TrendingUp className="w-4 h-4 text-green-500" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {new Date(day.date).toLocaleDateString()}
                        </span>
                      </div>
                      <div className={`${language === 'ar' ? 'text-left' : 'text-right'}`}>
                        <p className="font-medium text-gray-900 dark:text-white">SDG {day.revenue.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">{t('noRevenueData')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Real-time Performance */}
      {realtimeStats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {t('hourlyPerformance')}
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {realtimeStats.current.hourlyOrders} {t('orders')}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">SDG {realtimeStats.current.hourlyRevenue.toFixed(2)} {t('revenue')}
                </p>
              </div>
              <Activity className="w-8 h-8 text-blue-500" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {t('avgPrepTime')}
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {realtimeStats.performance.avgPreparationTime} {t('min')}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {realtimeStats.performance.completionRate.toFixed(1)}% {t('completionRate')}
                </p>
              </div>
              <Clock className="w-8 h-8 text-green-500" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {t('pendingOrders')}
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {realtimeStats.current.pendingOrders}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('needAttention')}
                </p>
              </div>
              <AlertCircle className="w-8 h-8 text-orange-500" />
            </div>
          </div>
        </div>
      )}

      {/* Recent Orders */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">
            {t('recentOrders')}
          </h2>
        </div>
        <div className="p-6">
          {dashboardData.recentOrders.length > 0 ? (
            <div className="space-y-4">
              {dashboardData.recentOrders.map((order) => (
                <div
                  key={order._id}
                  className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg"
                >
                  <div className={`flex items-center ${language === 'ar' ? 'space-x-reverse space-x-4' : 'space-x-4'}`}>
                    <Package className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        Order #{order.orderNumber}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {order.customer?.name || t('guest')}
                      </p>
                    </div>
                  </div>
                  <div className={`${language === 'ar' ? 'text-left' : 'text-right'}`}>
                    <p className="font-medium text-gray-900 dark:text-white">SDG {order.pricing?.total?.toFixed(2)}
                    </p>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      order.status === "placed" ? "bg-yellow-100 text-yellow-800" :
                      order.status === "preparing" ? "bg-blue-100 text-blue-800" :
                      order.status === "ready" ? "bg-green-100 text-green-800" :
                      "bg-gray-100 text-gray-800"
                    }`}>
                      {order.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">{t('noRecentOrders')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default RestaurantDashboardPage 